import { useTheme } from '@repo/theme';
import ScreenNotFound from 'app/+not-found';
import { getNavigationThemes } from 'component/navigation/theme';
import { RenderNotificationToasts } from 'component/notification-toast';
import { LoadingSpinnerContainer } from 'component/progress/loading-spinner';
import { type RoutePath, Stack, useGlobalSearchParams, useLocalSearchParams } from 'expo-router';
import { ThemeProvider } from 'expo-router/react-navigation';
import { useMemo } from 'react';
import { I18nProvider } from 'react-aria';
import { resolveLanguage } from 'system/request-metadata';
import { SiloContext } from 'util/silo-context';
import { getDeploymentConfig } from 'util/util';

export function SuspenseFallback() {
    return <LoadingSpinnerContainer />;
}

/**
 * Renders the silo's stack on the first render, without suspending.
 *
 * The `(app)` layout sends a user to profile setup or the suspended page by
 * pushing that page onto this stack. The page the user asked for keeps its
 * place in history only if this stack has already rendered and committed its
 * state to the router.
 */
export default function SiloLayout() {
    const { siloKey } = useLocalSearchParams<Extract<RoutePath, `/[siloKey]/${string}`>>();
    const { language_g } = useGlobalSearchParams<{ language_g?: string | string[] }>();
    const urlLanguage = Array.isArray(language_g) ? language_g.at(-1) : language_g;
    const tokens = useTheme();
    const { transparentNavigationTheme } = useMemo(() => getNavigationThemes(tokens), [tokens]);

    const language = resolveLanguage({ siloKey, urlLanguage });

    if (!isInStaticConfig(siloKey)) {
        return <ScreenNotFound />;
    }

    return (
        <I18nProvider locale={language}>
            <SiloContext.Provider value={siloKey}>
                <ThemeProvider value={transparentNavigationTheme}>
                    <Stack screenOptions={{ headerShown: false }} />
                </ThemeProvider>
                <RenderNotificationToasts />
            </SiloContext.Provider>
        </I18nProvider>
    );
}

function isInStaticConfig(siloKey: string) {
    const staticSilos = getDeploymentConfig().allowedSilos;
    if (!staticSilos) {
        // If no static silos are defined, all silos are allowed
        return true;
    }

    return staticSilos.includes(siloKey);
}
